import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AtSign, Trash2 } from "lucide-react";

/**
 * AdminUsername — sets or clears the admin's custom sign-in username.
 * Case-sensitive, 3-32 chars, letters/digits/._-.
 * Backend: PUT /api/auth/username (admin only).
 */
export default function AdminUsername() {
  const { user, refresh } = useAuth();
  const currentUsername = user?.username || "";
  const [value, setValue] = useState(currentUsername);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.put("/auth/username", { username: value.trim() });
      toast.success(value.trim() ? `Sign in with "${value.trim()}"` : "Username cleared.");
      await refresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not save username");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!currentUsername) return;
    if (!window.confirm(`Remove username "${currentUsername}"? You'll have to sign in with your email again.`)) return;
    setBusy(true);
    try {
      await api.put("/auth/username", { username: "" });
      setValue("");
      toast.success("Username cleared.");
      await refresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not clear username");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mb-12" data-testid="admin-username-section">
      <h3 className="font-serif text-2xl mb-2">Sign-in username</h3>
      <p className="text-sm text-charcoal/75 leading-relaxed mb-6 max-w-lg">
        Set a short custom name to sign in with instead of your email — e.g. <span className="font-mono text-charcoal">gregb</span>. Your email still works too. Case-sensitive. 3-32 characters: letters, digits, and <span className="font-mono">.</span> <span className="font-mono">_</span> <span className="font-mono">-</span>.
      </p>
      <form onSubmit={save} className="border border-borderSubtle bg-white p-6 rounded-2xl space-y-5">
        <div>
          <label className="overline text-charcoal">Username</label>
          <div className="relative mt-2">
            <AtSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75" />
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="gregb"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              minLength={0}
              maxLength={32}
              pattern="[A-Za-z0-9._\-]{3,32}"
              className="pl-9 border border-borderSubtle bg-white focus-visible:outline-none focus-visible:border-oxblood w-full py-2 rounded font-mono text-sm"
              data-testid="admin-username-input"
            />
          </div>
          <p className="text-[12px] text-charcoal/75 mt-1.5">
            {currentUsername
              ? <>Currently signing in with <span className="font-mono text-charcoal">{currentUsername}</span></>
              : <>No username set — you sign in with your email address.</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={busy || value.trim() === currentUsername}
            className="px-5 py-2.5 bg-charcoal text-bone rounded-full text-[13px] uppercase tracking-[0.22em] hover:bg-oxblood transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-username-save"
          >
            {busy ? "Saving…" : currentUsername ? "Update username" : "Set username"}
          </button>
          {currentUsername && (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-oxblood/50 text-oxblood rounded-full text-[13px] uppercase tracking-[0.22em] hover:bg-oxblood hover:text-bone transition-colors disabled:opacity-40"
              data-testid="admin-username-clear"
            >
              <Trash2 size={12} /> Clear username
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
